/**
 * Form Access Guard
 * 
 * Route guard that enforces form-specific access control rules. Prevents users
 * from accessing forms they don't have permission to use, even if they navigate
 * directly via URL. Works in conjunction with FormModelService to check access
 * based on form metadata access control rules.
 * 
 * Access Control Flow:
 * 1. Extract form type from route parameters
 * 2. Get current user from EntraService
 * 3. Check access permissions via FormModelService
 * 4. Allow or deny access based on user permissions
 * 
 * Denial Behavior:
 * - Redirects unauthorized users to forms landing page
 * - Logs access denial for monitoring/debugging
 * - Can optionally display toast/alert with denial message
 * 
 * Access Rules Checked:
 * - User authentication status (must be logged in)
 * - Form-specific requirements (e.g., requiresPfizerAccess, requiresAdmin)
 * - Any custom access logic defined in form metadata
 * 
 * Usage in Routes:
 * Applied to individual form routes in app-routing.module.ts
 * Works alongside MsalGuard for authentication + authorization
 * 
 * @example
 * {
 *   path: 'pfizer-safety-plan',
 *   component: PfizerSafetyPlanComponent,
 *   canActivate: [MsalGuard, FormAccessGuard],
 *   data: { formType: 'pfizer-safety-plan' }
 * }
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Observable } from 'rxjs';
import { FormModelService } from '../services/form-model.service';
import { EntraService } from '../services/entra.service';

@Injectable({
  providedIn: 'root'
})
export class FormAccessGuard implements CanActivate {
  
  constructor(
    private formModelService: FormModelService,
    private entraService: EntraService,
    private router: Router
  ) {}

  /**
   * Determines if a route can be activated based on form access permissions
   * 
   * @param route - The activated route snapshot containing route parameters
   * @param state - The router state snapshot containing URL information
   * @returns true if user can access the form, false otherwise (redirects to forms list)
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    
    // Get form type from route data (set in routing configuration)
    const formType = route.data['formType'] as string | undefined;
    
    // Get current user from EntraService
    const user = this.entraService.user();

    // If no form type is specified in route data, allow access
    // This shouldn't happen in normal operation, but provides graceful fallback
    if (!formType) {
      console.warn('FormAccessGuard: No formType specified in route data');
      return true;
    }

    // Check if user has access to this form type
    const hasAccess = this.formModelService.userCanAccessForm(formType, user);

    if (!hasAccess) {
      // Get the access denied message for logging/display
      const deniedMessage = this.formModelService.getAccessDeniedMessage(formType);
      
      // Log the access denial for monitoring/debugging
      console.warn(
        `FormAccessGuard: Access denied to form "${formType}"`,
        {
          user: user?.email || 'Not logged in',
          reason: deniedMessage,
          attemptedUrl: state.url
        }
      );

      // TODO: Optional - Display toast/alert with denial message
      // this.toastService.showError(deniedMessage);

      // Redirect to forms landing page
      return this.router.createUrlTree(['/forms']);
    }

    // User has access - allow navigation
    return true;
  }
}